// Rich per-product page configuration. Each entry drives one <ProductPage />.
// Content is derived from the live product sites the client shared.

export const PRODUCT_CONFIGS = {
  "acadmax-erp": {
    slug: "acadmax-erp",
    name: "Acadmax",
    kicker: "Acadmax · Higher-Education ERP",
    brand: {
      accent: "#f59e0b",
      gradientFrom: "#0f172a",
      gradientTo: "#7c2d12",
      hero: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80",
    },
    hero: {
      title: "The campus ERP that finally",
      highlight: "gets higher education.",
      subtitle:
        "Acadmax runs the entire student lifecycle — from admissions to alumni — across schools, colleges and universities. 20+ modules, one platform, engineered for scale.",
      tagline: "20+ modules · admissions to degree issuance",
      stats: [
        { v: "20+", l: "Modules" },
        { v: "500K+", l: "Students onboarded" },
        { v: "100%", l: "Web-based · cloud" },
        { v: "24/7", l: "Support & uptime" },
      ],
    },
    clientsEyebrow: "Universities · colleges · medical schools running Acadmax",
    clients: [
      { name: "Quaid-e-Azam University" },
      { name: "NUML — National University of Modern Languages" },
      { name: "Rehman Medical College" },
      { name: "Bahria University" },
      { name: "COMSATS University" },
      { name: "Riphah International University" },
      { name: "University of Sargodha" },
      { name: "Islamia University Bahawalpur" },
      { name: "Government College University" },
      { name: "Preston University" },
      { name: "Fatima Jinnah Women University" },
      { name: "IBA Karachi" },
    ],
    sections: {
      features: {
        kicker: "Why Acadmax",
        title: "Built only for higher education — never a repurposed ERP.",
        subtitle:
          "Unlike generic back-office ERPs, Acadmax is engineered around the realities of admissions, academics and student services in modern institutions.",
        items: [
          { icon: "GraduationCap", title: "Acadmax Focus", desc: "Solely purpose-built for higher education — every screen speaks the language of academics." },
          { icon: "Route", title: "Full Student Life Cycle", desc: "Recruit, admit, teach, assess, graduate and stay in touch with alumni — one system." },
          { icon: "Activity", title: "Track Student Activity", desc: "Session activity, involvement history and schedule conflicts at a glance." },
          { icon: "MessagesSquare", title: "Manage Counseling", desc: "Auto-log parent communication, financial standing and campus driving records." },
          { icon: "ListTodo", title: "Managing Students", desc: "Multi-task management with milestones, reminders and attached documents." },
          { icon: "Compass", title: "Guide Students", desc: "Interests, resumes, employer routing and upcoming interview boards." },
          { icon: "Users", title: "Teacher Benefits", desc: "Manage classes, schedules, results, online + offline exam papers per project." },
          { icon: "Cloud", title: "Seamless Online Campus", desc: "Removes barriers so you can manage students from recruit to graduation — anywhere." },
        ],
      },
      modules: {
        kicker: "20+ modules",
        title: "From admission test to degree issuance.",
        subtitle:
          "Every process a modern institution runs — pre-integrated, secure, and scalable to the largest multi-campus universities.",
        items: [
          { title: "Admissions", desc: "Online application forms, admission tests, merit lists and offer letters." },
          { title: "Fee Management", desc: "Fee heads, vouchers, scholarships, refunds and gateway integration." },
          { title: "Academics", desc: "Programs, courses, sections, timetables and attendance." },
          { title: "Examination", desc: "Paper generation, results, transcripts and degree issuance." },
          { title: "HR & Payroll", desc: "Staff records, payroll runs, leaves, appraisals and PF/EOBI." },
          { title: "Finance & Accounts", desc: "Ledger, budgets, purchase, receivables and audit reports." },
          { title: "Library", desc: "Cataloguing, circulation, digital assets and patron portal." },
          { title: "Alumni & CRM", desc: "Lifetime engagement, events, donations and mentorship pipelines." },
        ],
      },
      steps: {
        kicker: "How Acadmax fits",
        title: "A rollout playbook that respects your calendar.",
        subtitle: "Concierge onboarding — data migration, training and go-live inside a single academic term.",
        items: [
          { title: "Discovery", desc: "Blueprint every workflow, integration and role across your campuses." },
          { title: "Configure", desc: "Programs, fees, users, roles, exam patterns and reports — set up with you." },
          { title: "Migrate", desc: "Bring your legacy records in cleanly with a verified data pipeline." },
          { title: "Go Live", desc: "Train, launch, and stay supported by a dedicated success manager." },
        ],
      },
      testimonials: {
        kicker: "Trusted by institutions",
        title: "Institutions that switched to Acadmax stay.",
        items: [
          { quote: "Acadmax gave our registrar's office a decade back. Admissions, fees, exams — one system, actually working.", author: "Dr. Faisal Ahmed", role: "Registrar · Private University" },
          { quote: "Our parents finally trust the numbers on the report cards. Nothing is manual anymore.", author: "Sana Iqbal", role: "Principal · K-12 Group of Schools" },
          { quote: "The CloudLab team implemented in one term. Support has been outstanding ever since.", author: "Prof. Iqbal Hussain", role: "Vice Chancellor · Public Sector University" },
        ],
      },
    },
  },

  "libmax": {
    slug: "libmax",
    name: "Libmax",
    kicker: "Libmax · Library Management System",
    brand: {
      accent: "#10b981",
      gradientFrom: "#022c22",
      gradientTo: "#0f766e",
      hero: "https://images.unsplash.com/photo-1521587760476-6c12a4b040da?auto=format&fit=crop&w=1200&q=80",
    },
    hero: {
      title: "The library platform that runs",
      highlight: "on autopilot.",
      subtitle:
        "Libmax is a comprehensive automation solution for library systems — cataloguing, circulation, digital assets and every material (print, electronic, digital) in one place.",
      tagline: "One dashboard · print · digital · everything in between",
      stats: [
        { v: "8+", l: "Core modules" },
        { v: "1M+", l: "Books catalogued" },
        { v: "RFID", l: "Ready" },
        { v: "24/7", l: "Patron self-service" },
      ],
    },
    clientsEyebrow: "Libraries running on Libmax",
    clients: [
      { name: "Quaid-e-Azam University Library" },
      { name: "NUML Central Library" },
      { name: "Rehman Medical Library" },
      { name: "COMSATS Library System" },
      { name: "Bahria University Library" },
      { name: "GC University Library" },
      { name: "Islamia University Library" },
      { name: "IBA Karachi Library" },
      { name: "Preston University Library" },
      { name: "Fatima Jinnah Women University Library" },
    ],
    sections: {
      features: {
        kicker: "Why Libmax",
        title: "The ultimate library management solution.",
        subtitle:
          "Dedicated to the most inventive library workflows, Libmax gives your team superpowers with a single, well-designed platform.",
        items: [
          { icon: "LayoutDashboard", title: "Intuitive Dashboard", desc: "One place to manage books issued, profile tools and advanced search." },
          { icon: "UserCog", title: "Profile Manager", desc: "Control every aspect of user profiles and roles centrally." },
          { icon: "ShieldCheck", title: "Policy Manager", desc: "Fine-grained access to issue, reissue and reserve — by role." },
          { icon: "Files", title: "Digital Asset", desc: "Curate eBooks, journals and multimedia with the same catalog UX." },
          { icon: "Megaphone", title: "Library News", desc: "Publish announcements that land on every member's dashboard automatically." },
          { icon: "MessagesSquare", title: "Discussion Boards", desc: "Members create topics and reply — build a real reading community." },
          { icon: "Barcode", title: "Cataloguing", desc: "Add / edit / remove books with auto-generated accession numbers." },
          { icon: "BarChart3", title: "Reports", desc: "In-depth reports from issued books to lost-and-found — export ready." },
        ],
      },
      modules: {
        kicker: "Modules",
        title: "Everything a modern librarian needs.",
        subtitle: "From accession to analytics — Libmax scales from a single school to a multi-campus university.",
        items: [
          { title: "Circulation", desc: "Issue, return, reserve and fines — with RFID and barcode workflows." },
          { title: "Cataloguing", desc: "MARC-compatible bibliographic records with rich metadata." },
          { title: "Digital Library", desc: "eBooks, journals, videos and course reserves in a unified UI." },
          { title: "Patron Portal", desc: "Members search, reserve, renew and pay fines — from any device." },
          { title: "OPAC Search", desc: "Fast, faceted public catalogue with holds and recommendations." },
          { title: "Inventory", desc: "Shelf-order audits, weeding lists and location transfers." },
          { title: "Acquisitions", desc: "Vendor management, orders, receipts and budget tracking." },
          { title: "Analytics", desc: "Usage patterns, popular titles, and collection-development insights." },
        ],
      },
      steps: {
        kicker: "How it works",
        title: "Live in weeks, not quarters.",
        items: [
          { title: "Discovery", desc: "Understand your libraries, holdings and workflows." },
          { title: "Migrate", desc: "Import catalogs, patrons and history without downtime." },
          { title: "Configure", desc: "Roles, policies, fine rules, digital assets and reports." },
          { title: "Go live", desc: "Train staff and welcome patrons to a modern library." },
        ],
      },
      testimonials: {
        kicker: "Loved by librarians",
        title: "The librarians who use it can't imagine going back.",
        items: [
          { quote: "Libmax turned our library into a self-serve, data-driven part of the campus.", author: "Nabila Rehman", role: "Head Librarian · University Library" },
          { quote: "RFID and cataloguing that used to eat our week now takes an afternoon.", author: "Kashif Aslam", role: "Systems Librarian · Public Sector" },
          { quote: "Our digital assets are finally as easy to find as our print books.", author: "Amna Hassan", role: "Deputy Librarian · Private College" },
        ],
      },
    },
  },

  "unicrm-ai": {
    slug: "unicrm-ai",
    name: "UniCRM.ai",
    kicker: "UniCRM.ai · AI-native admissions CRM",
    external_url: "https://unicrm.ai/",
    hero_image: "https://images.unsplash.com/photo-1600880292203-757bb62b4baf?auto=format&fit=crop&w=1200&q=80",
    brand: {
      accent: "#a855f7",
      gradientFrom: "#1e1b4b",
      gradientTo: "#701a75",
      hero: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1200&q=80",
    },
    hero: {
      title: "Recruit, vet & enrol students with",
      highlight: "3 AI Agents on your team.",
      subtitle:
        "UniCRM.ai runs your WhatsApp inbox, builds applications, vets documents and routes leads — so your counsellors close more, faster. Built for education consultancies, university partners and admissions BPOs.",
      tagline: "AI Counsellor · Admissions · Customer Care",
      stats: [
        { v: "50K+", l: "Applications tracked" },
        { v: "3", l: "AI agents per workspace" },
        { v: "24/7", l: "WhatsApp automation" },
        { v: "60+", l: "Countries supported" },
      ],
    },
    clientsEyebrow: "Education consultancies · universities · admissions BPOs",
    clients: [
      { name: "Global Education Consultants" },
      { name: "StudyAbroad Pro" },
      { name: "EduPath Advisory" },
      { name: "Times Consultants" },
      { name: "Overseas Education Network" },
      { name: "Study Group Partners" },
      { name: "Prime Education UK" },
      { name: "Applyboard Partner Network" },
      { name: "AECC Global" },
      { name: "SI-UK Admissions" },
    ],
    sections: {
      features: {
        kicker: "Features",
        title: "Everything you need to succeed.",
        subtitle: "Purpose-built for education consultants to manage applications, capture leads and grow their business.",
        items: [
          { icon: "MessageCircle", title: "WhatsApp Automation", desc: "Two-way chat with AI-suggested replies, broadcasts and automated lead routing — one inbox." },
          { icon: "Bot", title: "3 Specialised AI Agents", desc: "Counsellor (course match), Admissions (application build), Customer Care (status) — each trained on your data." },
          { icon: "LayoutList", title: "Desk-Based Workflow", desc: "Move applications across counsellor, admissions, finance and visa desks with role-based access." },
          { icon: "Building2", title: "BPO Services Module", desc: "White-label admissions support — assign overflow to BPO teams with their own portal, KPIs and billing." },
          { icon: "Plug", title: "Lead Integration Hub", desc: "Facebook Lead Ads, Google Sheets, web forms and inbound API — every lead lands in one funnel." },
          { icon: "ShieldCheck", title: "Role-Based Access", desc: "Granular permissions for counsellors, managers, BPO partners and super-admins with multi-tenant isolation." },
          { icon: "FileSearch", title: "AI Document Vetting", desc: "Auto-extract passports, transcripts and English certs. Catch gaps and missing pages before submission." },
          { icon: "BarChart3", title: "Analytics & Reports", desc: "Custom report builder, team KPIs, conversion funnels and country-level insights." },
        ],
      },
      modules: {
        kicker: "The stack",
        title: "One platform for enquiry → enrolment.",
        subtitle: "Every step your team runs today — inbox, application, documents, visa, finance — coordinated in one system.",
        items: [
          { title: "Omni-channel Inbox", desc: "WhatsApp, email, web chat, calls — unified thread per student." },
          { title: "Application Builder", desc: "AI drafts programmes, personal statements and financial forms." },
          { title: "Document Locker", desc: "Encrypted vault with automatic tagging and expiry alerts." },
          { title: "Desk Router", desc: "Counsellor → Admissions → Finance → Visa — with SLAs each." },
          { title: "Broadcast Studio", desc: "Segment students and campaign on WhatsApp/email in minutes." },
          { title: "Universities Catalog", desc: "Live programmes, deadlines and eligibility — always current." },
          { title: "BPO Portal", desc: "Assign overflow with KPIs, billing and audit trail." },
          { title: "Insights", desc: "Country/programme funnels, team leaderboards and forecasts." },
        ],
      },
      steps: {
        kicker: "How it works",
        title: "Get your consultancy live in minutes.",
        items: [
          { title: "Sign up & configure", desc: "Create workspace, add team, add universities and programmes." },
          { title: "Capture leads", desc: "Plug in ad accounts and web forms — every enquiry lands here." },
          { title: "Process applications", desc: "Move across desks, upload docs and track SLA per student." },
          { title: "Close & celebrate", desc: "Enrolment done, commissions logged, happy students onboarded." },
        ],
      },
      testimonials: {
        kicker: "Loved by education professionals",
        title: "What our customers say about UniCRM.ai.",
        items: [
          { quote: "UniCRM.ai transformed how we manage student applications. The desk workflow is brilliant!", author: "Sarah Johnson", role: "Director · Global Education Consultants" },
          { quote: "The AI counsellor saves us hours every day. It handles eligibility checks automatically.", author: "Michael Chen", role: "Senior Counsellor · StudyAbroad Pro" },
          { quote: "Finally, a CRM that understands education consultants. The lead integration is seamless.", author: "Priya Sharma", role: "Founder · EduPath Advisory" },
        ],
      },
    },
  },

  "visainterviews-ai": {
    slug: "visainterviews-ai",
    name: "VisaInterviews.ai",
    kicker: "VisaInterviews.ai · AI interview coaching",
    external_url: "https://visainterviews.ai/",
    hero_image: "https://customer-assets-jai6qajn.emergentagent.net/job_ai-solutions-lab-22/artifacts/0efre3uu_image.png",
    brand: {
      accent: "#fb923c",
      gradientFrom: "#1c1917",
      gradientTo: "#7c2d12",
      hero: "https://images.pexels.com/photos/4226140/pexels-photo-4226140.jpeg?auto=compress&cs=tinysrgb&w=1200",
    },
    hero: {
      title: "Practise your visa interview.",
      highlight: "Pass it on the first try.",
      subtitle:
        "A smart way to prepare for CAS Shield interviews, immigration interviews and IELTS Speaking tests — in a real interview environment powered by AI.",
      tagline: "Realistic AI simulations · instant scoring · country-specific banks",
      stats: [
        { v: "3.7k+", l: "Interviews conducted" },
        { v: "95%", l: "Success rate" },
        { v: "4.9/5", l: "Student rating" },
        { v: "24/7", l: "Available anytime" },
      ],
    },
    clientsEyebrow: "Trusted by students · agents · universities",
    clients: [
      { name: "University of Toronto" },
      { name: "London School of Economics" },
      { name: "University of Manchester" },
      { name: "University of Warwick" },
      { name: "University of Leeds" },
      { name: "Coventry University" },
      { name: "Study Group Partners" },
      { name: "Kaplan International Pathways" },
      { name: "Global Education Consultants" },
      { name: "Times Consultants" },
    ],
    sections: {
      features: {
        kicker: "Features",
        title: "Everything you need to ace it.",
        subtitle: "A comprehensive platform with every tool for preparing and passing credibility interviews.",
        items: [
          { icon: "Sparkles", title: "AI-Powered Assessment", desc: "Advanced AI scores your answers for keyword coverage, clarity and relevance." },
          { icon: "Video", title: "Realistic Simulation", desc: "Real interview conditions — timed questions, video recording, live pressure." },
          { icon: "ScanFace", title: "Identity Verification", desc: "Secure multi-step verification including facial recognition and liveness." },
          { icon: "Award", title: "Detailed Scoring", desc: "Per-question scores, transcripts and improvement suggestions." },
          { icon: "GraduationCap", title: "University Partnerships", desc: "Trusted by leading universities worldwide for credibility interviews." },
          { icon: "Zap", title: "Instant Results", desc: "Receive results and transcripts immediately after completion." },
        ],
      },
      modules: {
        kicker: "Interview room",
        title: "A professional interview experience.",
        subtitle: "Our interview room provides a realistic simulation with every feature for a successful interview.",
        items: [
          { title: "HD Video Recording", desc: "Studio-quality capture with pass-through preview." },
          { title: "Clear Audio Capture", desc: "Noise-cancelled inputs so nothing gets missed by the AI." },
          { title: "Timed Questions", desc: "30-second read + 90-second answer windows — just like the real thing." },
          { title: "Instant Transcription", desc: "Word-perfect transcripts to review your delivery calmly." },
          { title: "Country Banks", desc: "US, UK, Canada, Schengen — sourced from real consular questions." },
          { title: "Coach Reports", desc: "Share detailed reports with your education agent or coach." },
          { title: "Retake Anytime", desc: "Practise until you're consistent — no limits on retakes." },
          { title: "Data Security", desc: "Encrypted at rest and in transit — GDPR-ready by design." },
        ],
      },
      steps: {
        kicker: "How it works",
        title: "Get started in minutes.",
        items: [
          { title: "Sign up & choose plan", desc: "Create your account and select a package that fits your needs." },
          { title: "Complete verification", desc: "Verify identity through our secure multi-step process." },
          { title: "Take your interview", desc: "Answer questions in a simulated interview environment." },
          { title: "Get results", desc: "Receive detailed AI-powered assessment and feedback." },
        ],
      },
      testimonials: {
        kicker: "What our users say",
        title: "Students and agents love the platform.",
        items: [
          { quote: "VisaInterviews.ai helped me prepare for my credibility interview with confidence. The AI feedback was incredibly helpful!", author: "Sarah Chen", role: "Student · University of Toronto" },
          { quote: "As an agent, this platform has revolutionised how I prepare my students. The detailed analytics are invaluable.", author: "Michael O'Brien", role: "Education Agent" },
          { quote: "The realistic simulation and immediate feedback helped me identify my weak areas before the actual interview.", author: "Priya Sharma", role: "Student · LSE" },
        ],
      },
    },
  },

  "teachingsoftware-ai": {
    slug: "teachingsoftware-ai",
    name: "TeachingSoftware.ai",
    kicker: "TeachingSoftware.ai · Teaching OS",
    external_url: "https://teachingsoftware.ai/",
    hero_image: "https://images.unsplash.com/photo-1523240795612-9a054b0db644?auto=format&fit=crop&w=1200&q=80",
    brand: {
      accent: "#8b5cf6",
      gradientFrom: "#0b1120",
      gradientTo: "#4c1d95",
      hero: "https://images.pexels.com/photos/14314636/pexels-photo-14314636.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940",
    },
    hero: {
      title: "The teaching platform engineered for",
      highlight: "online tutors.",
      subtitle:
        "TeachingSoftware.ai is the AI teaching OS that builds your classes, generates quizzes, plans lessons, runs your live sessions and grows your tuition business — around the clock.",
      tagline: "The intelligent tutor that never sleeps.",
      stats: [
        { v: "8", l: "Modules · one platform" },
        { v: "24/7", l: "AI tutor availability" },
        { v: "1000+", l: "Students / institute" },
        { v: "92%", l: "Avg. engagement" },
      ],
    },
    clientsEyebrow: "Institutes · coaching centres · online tutors",
    clients: [
      { name: "Greenfield International School" },
      { name: "BrightPath Academy" },
      { name: "Cambridge Tutors Karachi" },
      { name: "EduFusion Online" },
      { name: "MathMasters Coaching" },
      { name: "STEM Academy Islamabad" },
      { name: "ScholarHub Tutorial" },
      { name: "Elite Coaching Centre" },
      { name: "Aspire Learning Group" },
      { name: "Prime Academy Dubai" },
    ],
    sections: {
      features: {
        kicker: "Powerful platform",
        title: "The intelligent tutor that never sleeps.",
        subtitle:
          "Picks up homework at 2 AM. Drafts the next lesson before your morning coffee. Flags weak topics before the parent does.",
        items: [
          { icon: "Wand2", title: "AI Lesson Builder", desc: "Paste your syllabus. Get animated lessons, quizzes, homework and voice-over scripts in seconds." },
          { icon: "Video", title: "Live Class", desc: "Fully-featured live audio + video classroom — or plug into Meet, Teams and Zoom." },
          { icon: "PenLine", title: "Smart Whiteboard", desc: "Teach visually with a real-time collaborative whiteboard." },
          { icon: "Eye", title: "Engagement AI", desc: "Concentration analysis every 30 seconds — spot the student slipping in week 2." },
          { icon: "MessagesSquare", title: "24/7 AI Tutor", desc: "When you step out, AI steps in — knows the syllabus, knows every student." },
          { icon: "Mic", title: "Auto-Transcripts", desc: "Recorded lectures come with searchable transcripts and highlights." },
          { icon: "BookOpen", title: "Storybook on Demand", desc: "AI animated stories and visuals — great for younger learners." },
          { icon: "Search", title: "Students from Google", desc: "A public tutor profile + lead capture that ranks and converts." },
        ],
      },
      modules: {
        kicker: "For institutes & coaching centres",
        title: "Schools run smoother. Parents sleep better.",
        subtitle:
          "One operating system for the whole institute — dozens of teachers, hundreds of students, and the AI guardrails that let parents trust you.",
        items: [
          { title: "Attendance", desc: "Auto-marked the moment a student joins — parents pinged before they even ask." },
          { title: "AI Fallback", desc: "If the teacher is late or off, the AI Tutor takes the class in seconds." },
          { title: "Weekly Parent Digest", desc: "Saturday morning AI report — attendance, scores, weak topics, next steps." },
          { title: "Concentration Live", desc: "Focus scored per student, live — flag drops in real time." },
          { title: "Multi-Teacher Admin", desc: "Central console for principals — teachers, classes, insights, revenue." },
          { title: "AI Assistant", desc: "Ask for 5 MCQs, an infographic or homework — get it, publish it." },
          { title: "Post-Class Report", desc: "Engagement, confidence, coverage and recommended next lesson." },
          { title: "Universal Context", desc: "Every signal — attendance, quiz, fees, parent — compounds into a living student profile." },
        ],
      },
      steps: {
        kicker: "How it works",
        title: "From syllabus to sold-out class in a weekend.",
        items: [
          { title: "Paste your syllabus", desc: "Drop a chapter — AI builds lessons, quizzes and homework." },
          { title: "Publish your profile", desc: "A public tutor profile ranks on Google and captures leads." },
          { title: "Run live classes", desc: "Native live class with whiteboard, or plug into Meet/Teams/Zoom." },
          { title: "Grow with AI reports", desc: "Weekly parent digest keeps every family engaged — and paying." },
        ],
      },
      testimonials: {
        kicker: "Built with tutors",
        title: "The next era of tuition runs on TeachingSoftware.ai.",
        items: [
          { quote: "It plans my lessons and grades my quizzes. I do the teaching — the platform does the rest.", author: "Ahmad Raza", role: "Grade 9–12 Math Tutor" },
          { quote: "Parents finally get a clear weekly report. Trust — and retention — went up in a month.", author: "Layla Hassan", role: "Biology · Grade 9–10" },
          { quote: "One console for 12 teachers and 500+ students. The AI keeps every class on track.", author: "Principal · Greenfield International", role: "Institute admin" },
        ],
      },
    },
  },

  // Fall-back / existing product kept intact — still uses generic template if needed
  "hellosir-ai": {
    slug: "hellosir-ai",
    name: "HelloSir.ai",
    kicker: "HelloSir.ai · Enterprise AI concierge",
    external_url: "https://hellosir.ai/",
    hero_image: "https://images.unsplash.com/photo-1531482615713-2afd69097998?auto=format&fit=crop&w=1200&q=80",
    brand: {
      accent: "#06b6d4",
      gradientFrom: "#0c4a6e",
      gradientTo: "#1e293b",
      hero: "https://images.unsplash.com/photo-1758073519996-6d3c63b4922c?auto=format&fit=crop&w=1200&q=80",
    },
    hero: {
      title: "One AI concierge across",
      highlight: "web · voice · chat.",
      subtitle:
        "HelloSir.ai answers on your website, WhatsApp, phone and email — trained on your business, handing off to humans only when it matters.",
      tagline: "Web chat · voice · WhatsApp · email — one agent.",
      stats: [
        { v: "24/7", l: "Multi-channel coverage" },
        { v: "12+", l: "Languages" },
        { v: "1.8×", l: "Lead-to-booking lift" },
        { v: "99.9%", l: "Uptime SLA" },
      ],
    },
    clientsEyebrow: "In production across hospitality · healthcare · real estate",
    clients: [
      { name: "The Kensington Group" },
      { name: "MediCore Clinics" },
      { name: "Emirates Estates" },
      { name: "Whitmore Realty" },
      { name: "Luxora Hospitality" },
      { name: "Premier Health Network" },
      { name: "Riyadh FinTech" },
      { name: "Weber Logistik" },
    ],
    sections: {
      features: {
        kicker: "Features",
        title: "Everything a modern concierge needs.",
        items: [
          { icon: "Bot", title: "Web + Voice Widget", desc: "Drop-in chat + voice widget with brand-tuned personality." },
          { icon: "MessageCircle", title: "WhatsApp + Email", desc: "One agent across every channel — never miss a lead." },
          { icon: "Users", title: "CRM Handoff", desc: "Hot leads pushed straight into your CRM with full context." },
          { icon: "Target", title: "Lead Scoring", desc: "Real-time intent + budget scoring — route only the ready ones." },
          { icon: "Languages", title: "Multi-lingual", desc: "12+ languages out of the box." },
          { icon: "Shield", title: "Enterprise Ready", desc: "SOC-friendly, encrypted, GDPR-ready." },
        ],
      },
      modules: {
        kicker: "Modules",
        title: "Fits your funnel like it was built for it.",
        items: [
          { title: "Knowledge Base", desc: "Ingest docs, sitemaps and product data with one click." },
          { title: "Voice Persona", desc: "Studio-grade voices tuned to your brand." },
          { title: "Escalation Rules", desc: "Time, intent, VIPs, thresholds — hand off cleanly." },
          { title: "Analytics", desc: "Funnels, intents, resolutions, CSAT and revenue attribution." },
        ],
      },
      steps: {
        kicker: "How it works",
        title: "Live on your site this week.",
        items: [
          { title: "Ingest", desc: "Point it at your docs and site — we do the rest." },
          { title: "Tune", desc: "Voice, tone, escalation and channels in a single console." },
          { title: "Launch", desc: "Widget + WhatsApp + phone — go live in days." },
          { title: "Improve", desc: "Weekly insights that make the agent better every cycle." },
        ],
      },
      testimonials: {
        kicker: "In production",
        title: "Enterprises rely on HelloSir.ai.",
        items: [
          { quote: "HelloSir.ai replaced our entire first-response layer. Book rate is up 80%.", author: "Head of Growth", role: "Luxury Hospitality Group" },
          { quote: "One agent, five channels. It's the most productive hire we've made.", author: "COO", role: "Multi-clinic Healthcare Group" },
          { quote: "The lead scoring alone paid for the year in the first month.", author: "Sales Director", role: "Real Estate Brokerage" },
        ],
      },
    },
  },
};
